import { Dispatch, SetStateAction } from 'react';

import { Button, CheckCircleFillIcon, Divider, Input, Text, WarningFillIcon } from '@thefarmersfront/kitchen';

import { Bullet, FormField } from '~/components/common';
import { TypeDefaultInfoValue, TypeDefaultInfoValueState } from '~/pages/vender/confirm/VenderConfirmPage';

import { useKakaoPostcodeSearch } from '~/hooks/common';
import { validateNumberAndHyphenOnly } from '~/utils';

import { DEFAULT_INPUT_STATUS } from '~/constants';

import type { TypeOwnerDetail } from '~/types/vender.types';

import styles from './VenderConfirmDefaultInfoSection.module.scss';

type TypeVenderConfirmDefaultInfoSectionProps = {
  hasEditPermission: boolean;
  venderInfo: TypeOwnerDetail | null;
  defaultInfoValue: TypeDefaultInfoValue;
  setDefaultInfoValue: Dispatch<SetStateAction<TypeDefaultInfoValue>>;
  defaultInfoValueState: TypeDefaultInfoValueState;
  setDefaultInfoValueState: Dispatch<SetStateAction<TypeDefaultInfoValueState>>;
};

/**
 * [화주사 정보 관리 > 화주사 정보] 기본 정보 영역
 */
const VenderConfirmDefaultInfoSection = ({
  hasEditPermission,
  venderInfo,
  defaultInfoValue,
  setDefaultInfoValue,
  defaultInfoValueState,
  setDefaultInfoValueState,
}: TypeVenderConfirmDefaultInfoSectionProps) => {
  const disabled = !hasEditPermission;

  const { handleZipCodeSearchOpen } = useKakaoPostcodeSearch({
    onComplete: (zipCode, address) => {
      setDefaultInfoValue({
        ...defaultInfoValue,
        zipCode,
        address,
      });
    },
  });

  return (
    <div className={styles['input-section']}>
      <div className={styles['input-section-title-wrapper']}>
        <Text size="xxlarge" weight="bold" element="p">
          기본 정보
        </Text>
      </div>
      <div>
        <FormField
          label="화주사코드"
          contents={
            <Input
              id="owner-code"
              type="text"
              size="small"
              maxWidth="271px"
              placeholder="입력"
              disabled
              value={venderInfo?.ownerCode ?? ''}
            />
          }
          labelClassName={styles['input-row-label']}
        />
        <FormField
          label="화주사명"
          required
          contents={
            <Input
              id="owner-name"
              type="text"
              size="small"
              maxWidth="271px"
              placeholder="입력"
              disabled
              value={venderInfo?.ownerName ?? ''}
            />
          }
          labelClassName={styles['input-row-label']}
        />
        <FormField
          label="사업자번호"
          required
          tooltip={{
            title: undefined,
            placement: 'bottom-left',
            maxWidth: undefined,
            description: (
              <div className={styles['business-number-tooltip']}>
                <p>아래의 경우 사업자 정보 조회가 불가능합니다.</p>
                <div>
                  <p className={styles['business-number-tooltip-detail']}>
                    <Bullet fill="#ffffff" /> 2인 미만의 사업장
                  </p>
                  <p className={styles['business-number-tooltip-detail']}>
                    <Bullet fill="#ffffff" /> 화주사의 사업자정보(API정보) 등록이전
                  </p>
                </div>
              </div>
            ),
          }}
          contents={
            <Input
              id="business-number"
              type="text"
              size="small"
              maxWidth="271px"
              placeholder="입력"
              disabled
              rightIcon={
                venderInfo?.businessNumberChecked === true ? (
                  <CheckCircleFillIcon color="#029568" />
                ) : (
                  <WarningFillIcon color="#FA622F" />
                )
              }
              value={venderInfo?.businessNumber ?? ''}
            />
          }
          labelClassName={styles['input-row-label']}
        />
        <FormField
          label="법인등록번호"
          contents={
            <Input
              id="corporate-number"
              type="text"
              size="small"
              maxWidth="271px"
              disabled
              value={venderInfo?.corporateNumber ?? ''}
            />
          }
          labelClassName={styles['input-row-label']}
        />
        <FormField
          label="업태"
          required
          contents={
            <Input
              id="business-conditions"
              type="text"
              size="small"
              maxWidth="271px"
              disabled={disabled}
              status={defaultInfoValueState.businessConditions.status}
              validationMessage={defaultInfoValueState.businessConditions.message ?? ''}
              placeholder="입력"
              value={defaultInfoValue.businessConditions}
              onFocus={() =>
                setDefaultInfoValueState((prev) => ({ ...prev, businessConditions: DEFAULT_INPUT_STATUS }))
              }
              onChange={(e) => setDefaultInfoValue({ ...defaultInfoValue, businessConditions: e.target.value })}
              onBlur={(e) => setDefaultInfoValue({ ...defaultInfoValue, businessConditions: e.target.value.trim() })}
            />
          }
          labelClassName={styles['input-row-label']}
        />
        <FormField
          label="업종"
          required
          contents={
            <Input
              id="business-category"
              type="text"
              size="small"
              maxWidth="271px"
              disabled={disabled}
              status={defaultInfoValueState.businessCategory.status}
              validationMessage={defaultInfoValueState.businessCategory.message ?? ''}
              placeholder="입력"
              value={defaultInfoValue.businessCategory}
              onFocus={() => setDefaultInfoValueState((prev) => ({ ...prev, businessCategory: DEFAULT_INPUT_STATUS }))}
              onChange={(e) => setDefaultInfoValue({ ...defaultInfoValue, businessCategory: e.target.value })}
              onBlur={(e) => setDefaultInfoValue({ ...defaultInfoValue, businessCategory: e.target.value.trim() })}
            />
          }
          labelClassName={styles['input-row-label']}
        />
        <FormField
          label="대표자명"
          required
          contents={
            <Input id="ceo-name" type="text" size="small" maxWidth="271px" disabled value={venderInfo?.ceoName ?? ''} />
          }
          labelClassName={styles['input-row-label']}
        />
      </div>
      <Divider color="gray100" />
      <div className={styles['vender-confirm-inputs-wrapper']}>
        <FormField
          label="우편번호"
          required
          contents={
            <>
              <Input
                id="zip-code"
                type="text"
                size="small"
                maxWidth="218px"
                disabled
                value={defaultInfoValue.zipCode}
              />
              <Button size="small" color="secondary" disabled={disabled} onClick={handleZipCodeSearchOpen}>
                검색
              </Button>
            </>
          }
          labelClassName={styles['input-row-label']}
          contentsClassName={styles['input-button-wrapper']}
        />
        <FormField
          label="기본주소"
          required
          contents={
            <Input id="address" type="text" size="small" maxWidth="365px" disabled value={defaultInfoValue.address} />
          }
          labelClassName={styles['input-row-label']}
        />
        <FormField
          label="상세주소"
          contents={
            <Input
              id="detail-address"
              type="text"
              size="small"
              maxWidth="365px"
              placeholder="입력"
              disabled={disabled}
              value={defaultInfoValue.detailAddress}
              onChange={(e) => setDefaultInfoValue({ ...defaultInfoValue, detailAddress: e.target.value })}
              onBlur={(e) => setDefaultInfoValue({ ...defaultInfoValue, detailAddress: e.target.value.trim() })}
            />
          }
          labelClassName={styles['input-row-label']}
        />
        <FormField
          label="대표번호"
          required
          contents={
            <Input
              id="tel"
              type="text"
              size="small"
              maxWidth="271px"
              disabled={disabled}
              status={defaultInfoValueState.tel.status}
              validationMessage={defaultInfoValueState.tel.message ?? ''}
              placeholder="- 포함 입력"
              value={defaultInfoValue.tel}
              onFocus={() => setDefaultInfoValueState((prev) => ({ ...prev, tel: DEFAULT_INPUT_STATUS }))}
              onChange={(e) => {
                const newValue = e.target.value;
                if (validateNumberAndHyphenOnly(newValue)) {
                  setDefaultInfoValue({ ...defaultInfoValue, tel: newValue });
                }
              }}
              onBlur={(e) => setDefaultInfoValue({ ...defaultInfoValue, tel: e.target.value.trim() })}
            />
          }
          labelClassName={styles['input-row-label']}
        />
        <FormField
          label="FAX번호"
          contents={
            <Input
              id="fax"
              type="text"
              size="small"
              maxWidth="271px"
              disabled={disabled}
              status={defaultInfoValueState.fax.status}
              validationMessage={defaultInfoValueState.fax.message ?? ''}
              placeholder="- 포함 입력"
              value={defaultInfoValue.fax}
              onFocus={() => setDefaultInfoValueState((prev) => ({ ...prev, fax: DEFAULT_INPUT_STATUS }))}
              onChange={(e) => {
                const newValue = e.target.value;
                if (validateNumberAndHyphenOnly(newValue)) {
                  setDefaultInfoValue({ ...defaultInfoValue, fax: newValue });
                }
              }}
              onBlur={(e) => setDefaultInfoValue({ ...defaultInfoValue, fax: e.target.value.trim() })}
            />
          }
          labelClassName={styles['input-row-label']}
        />
      </div>
      <Divider color="gray100" />
      <div className={styles['vender-confirm-inputs-wrapper']}>
        <FormField
          label="서비스 구분"
          required
          contents={
            <Input
              id="owner-type"
              type="text"
              size="small"
              maxWidth="271px"
              disabled
              value={venderInfo?.ownerType ?? ''}
            />
          }
          labelClassName={styles['input-row-label']}
        />
      </div>
    </div>
  );
};

export default VenderConfirmDefaultInfoSection;
